import React, { useState, useEffect } from 'react';

const CONFERENCE_API = process.env.REACT_APP_CONFERENCE_API_URL || 'http://localhost:8080/conference';
const REGISTRATION_API = process.env.REACT_APP_REGISTRATION_API_URL || 'http://localhost:8085';
const REVIEW_API = process.env.REACT_APP_REVIEW_API_URL || 'http://localhost:8082';

const ReviewManagement = () => {
  const [conferences, setConferences] = useState([]);
  const [selectedConferenceId, setSelectedConferenceId] = useState('');
  const [tracks, setTracks] = useState([]);
  const [selectedTrackId, setSelectedTrackId] = useState('');
  const [papers, setPapers] = useState([]);
  const [allPapers, setAllPapers] = useState([]);
  const [selectedPaper, setSelectedPaper] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [showReviewsModal, setShowReviewsModal] = useState(false);
  const [editingReviewId, setEditingReviewId] = useState(null);
  const [editedRating, setEditedRating] = useState('');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedStatusPaper, setSelectedStatusPaper] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [revisionFeedback, setRevisionFeedback] = useState('');
  const [chairPersonId, setChairPersonId] = useState(null);
  const [abstractMetadata, setAbstractMetadata] = useState(null);

  useEffect(() => {
    fetchActiveConferences();
    // Get chairperson ID from localStorage
    const userData = localStorage.getItem('adminUser');
    if (userData) {
      const user = JSON.parse(userData);
      setChairPersonId(user.id);
    }
  }, []);

  const fetchActiveConferences = async () => {
    try {
      console.log('Fetching conferences from:', `${CONFERENCE_API}/active`);
      const response = await fetch(`${CONFERENCE_API}/active`);
      console.log('Conference response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('Conferences data:', data);
        setConferences(data);
      } else {
        console.error('Failed to fetch conferences:', response.status);
        showAlert('Failed to fetch conferences', 'error');
      }
    } catch (error) {
      console.error('Error fetching conferences:', error);
      showAlert('Error connecting to conference service', 'error');
    }
  };

  const fetchTracks = async (conferenceId) => {
    try {
      const response = await fetch(`${CONFERENCE_API}/api/tracks/conference/${conferenceId}`);
      if (response.ok) {
        const data = await response.json();
        console.log('Tracks data:', data);
        setTracks(data);
      } else {
        console.error('Failed to fetch tracks:', response.status);
        setTracks([]);
      }
    } catch (error) {
      console.error('Error fetching tracks:', error);
      setTracks([]);
    }
  };

  const fetchPapers = async (conferenceId) => {
    if (!conferenceId) return;

    setLoading(true);
    try {
      // Fetch tracks for the conference
      await fetchTracks(conferenceId);
      
      const selectedConference = conferences.find(conf => conf.id === parseInt(conferenceId));
      if (!selectedConference) {
        showAlert('Conference not found', 'error');
        setLoading(false);
        return;
      }
      
      // Fetch abstract metadata to check visibility settings
      try {
        const metadataResponse = await fetch(`${CONFERENCE_API}/api/abstract-metadata/conference/${conferenceId}`);
        if (metadataResponse.ok) {
          const metadata = await metadataResponse.json();
          const metadataObj = Array.isArray(metadata) ? metadata[0] : metadata;
          setAbstractMetadata(metadataObj);
        }
      } catch (err) {
        console.error('Failed to fetch abstract metadata:', err);
        setAbstractMetadata(null);
      }
      
      // Fetch papers from registration service (includes abstractText when using conference name)
      const response = await fetch(`${REGISTRATION_API}/api/papers/conference/${encodeURIComponent(selectedConference.name)}`);
      if (response.ok) {
        const papersData = await response.json();
        
        // Fetch review counts for each paper
        const papersWithReviewCounts = await Promise.all(
          papersData.map(async (paper) => {
            try {
              const reviewsResponse = await fetch(`${REVIEW_API}/review/reviews/paper/${paper.id}`);
              if (reviewsResponse.ok) {
                const reviewsList = await reviewsResponse.json();
                return { ...paper, reviewCount: reviewsList.length, reviews: reviewsList };
              }
            } catch (error) {
              console.error(`Failed to fetch reviews for paper ${paper.id}:`, error);
            }
            return { ...paper, reviewCount: 0, reviews: [] };
          })
        );
        
        setAllPapers(papersWithReviewCounts);
        setPapers(papersWithReviewCounts);
      } else {
        setPapers([]);
        showAlert('Failed to fetch papers', 'error');
      }
    } catch (error) {
      console.error('Error fetching papers:', error);
      setPapers([]);
      showAlert('Error fetching papers', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedConferenceId) {
      setSelectedTrackId('');
      fetchPapers(selectedConferenceId);
    } else {
      setPapers([]);
      setAllPapers([]);
      setTracks([]);
      setSelectedTrackId('');
    }
  }, [selectedConferenceId]);

  useEffect(() => {
    // Filter papers by selected track
    if (selectedTrackId) {
      console.log('Filtering by track:', selectedTrackId, 'from', allPapers.length, 'papers');
      const filtered = allPapers.filter(paper => paper.trackId == selectedTrackId);
      console.log('Filtered papers:', filtered.length);
      setPapers(filtered);
    } else {
      console.log('Showing all papers:', allPapers.length);
      setPapers(allPapers);
    }
  }, [selectedTrackId, allPapers]);

  const showAlert = (message, type = 'success') => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 5000);
  };

  const handleViewReviews = async (paper) => {
    console.log('Opening reviews for paper:', paper);
    console.log('Paper abstractText:', paper.abstractText);
    setSelectedPaper(paper);
    setLoading(true);
    try {
      const response = await fetch(`${REVIEW_API}/review/reviews/paper/${paper.id}`);
      if (response.ok) {
        const data = await response.json();
        setReviews(data);
        setShowReviewsModal(true);
      } else {
        showAlert('Failed to fetch reviews', 'error');
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
      showAlert('Error fetching reviews', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseReviewsModal = () => {
    setShowReviewsModal(false);
    setSelectedPaper(null);
    setReviews([]);
    setEditingReviewId(null);
    setEditedRating('');
  };

  const handleEditReview = (review) => {
    setEditingReviewId(review.id);
    setEditedRating(review.rating);
  };

  const handleCancelEdit = () => {
    setEditingReviewId(null);
    setEditedRating('');
  };

  const handleChangeStatus = (paper) => {
    setSelectedStatusPaper(paper);
    setNewStatus(paper.status);
    setRevisionFeedback(''); // Clear previous feedback
    setShowStatusModal(true);
  };

  const handleSaveStatus = async () => {
    if (!newStatus) {
      showAlert('Please select a status', 'error');
      return;
    }

    // Validate feedback for revision requests
    if ((newStatus === 'MINOR_REVISION' || newStatus === 'MAJOR_REVISION') && !revisionFeedback.trim()) {
      showAlert('Please provide feedback for the revision request', 'error');
      return;
    }

    setLoading(true);
    try {
      // Update paper status
      const statusResponse = await fetch(`${REGISTRATION_API}/api/papers/${selectedStatusPaper.id}/status?status=${encodeURIComponent(newStatus)}`, {
        method: 'PUT'
      });

      if (!statusResponse.ok) {
        showAlert('Failed to update paper status', 'error');
        setLoading(false);
        return;
      }

      // If revision is requested, create a review record with feedback
      if ((newStatus === 'MINOR_REVISION' || newStatus === 'MAJOR_REVISION') && chairPersonId) {
        const reviewData = {
          paperId: selectedStatusPaper.id,
          reviewerId: chairPersonId,
          rating: 5, // Neutral rating for chair feedback
          comments: revisionFeedback,
          recommendation: newStatus
        };

        const reviewResponse = await fetch(`${REVIEW_API}/review/reviews`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(reviewData)
        });

        if (!reviewResponse.ok) {
          console.error('Failed to create review record');
        }

        // Send email notification to author
        try {
          await fetch(`${REGISTRATION_API}/api/papers/${selectedStatusPaper.id}/notify-revision`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              status: newStatus,
              feedback: revisionFeedback
            })
          });
        } catch (emailError) {
          console.error('Failed to send email notification:', emailError);
        }
      }

      showAlert('Paper status updated successfully!', 'success');
      setShowStatusModal(false);
      setRevisionFeedback('');
      fetchPapers(selectedConferenceId);
    } catch (error) {
      console.error('Error updating paper status:', error);
      showAlert('Error updating paper status', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveOverride = async (reviewId) => {
    const rating = parseInt(editedRating);
    if (isNaN(rating) || rating < 1 || rating > 10) {
      showAlert('Rating must be between 1 and 10', 'error');
      return;
    }

    setLoading(true);
    try {
      const reviewToUpdate = reviews.find(r => r.id === reviewId);
      const response = await fetch(`${REVIEW_API}/review/reviews/${reviewId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...reviewToUpdate,
          rating: rating
        })
      });

      if (response.ok) {
        showAlert('Review score updated successfully!', 'success');
        setEditingReviewId(null);
        setEditedRating('');
        // Refresh reviews in modal
        const reviewsResponse = await fetch(`${REVIEW_API}/review/reviews/paper/${selectedPaper.id}`);
        if (reviewsResponse.ok) {
          const updatedReviews = await reviewsResponse.json();
          setReviews(updatedReviews);
        }
        // Refresh papers list to update average rating in table
        fetchPapers(selectedConferenceId);
      } else {
        const errorText = await response.text();
        showAlert(errorText || 'Failed to update review', 'error');
      }
    } catch (error) {
      console.error('Error updating review:', error);
      showAlert('Error updating review', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'SUBMITTED': '#17a2b8',
      'UNDER_REVIEW': '#ffc107',
      'ACCEPTED': '#28a745',
      'REJECTED': '#dc3545',
      'MINOR_REVISION': '#fd7e14',
      'MAJOR_REVISION': '#e83e8c'
    };
    return colors[status] || '#6c757d';
  };

  const getRecommendationColor = (recommendation) => {
    const colors = {
      'ACCEPT': '#28a745',
      'REJECT': '#dc3545',
      'MINOR_REVISION': '#ffc107',
      'MAJOR_REVISION': '#fd7e14'
    };
    return colors[recommendation] || '#6c757d';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const calculateAverageRating = (reviewsList) => {
    if (!reviewsList || reviewsList.length === 0) return 'N/A';
    const sum = reviewsList.reduce((acc, review) => acc + (review.rating || 0), 0);
    return (sum / reviewsList.length).toFixed(1);
  };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ marginBottom: '20px' }}>
        <h2>Review Management (Chairperson)</h2>
        <p style={{ color: '#666', marginTop: '8px' }}>
          View all reviews for submitted papers
        </p>
      </div>

      {alert && (
        <div style={{
          padding: '12px',
          marginBottom: '20px',
          borderRadius: '4px',
          backgroundColor: alert.type === 'success' ? '#d4edda' : '#f8d7da',
          color: alert.type === 'success' ? '#155724' : '#721c24',
          border: `1px solid ${alert.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          {alert.message}
        </div>
      )}

      <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
        <div style={{ flex: 1 }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            Select Conference:
          </label>
          <select
            value={selectedConferenceId}
            onChange={(e) => setSelectedConferenceId(e.target.value)}
            style={{
              width: '100%',
              padding: '10px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          >
            <option value="">-- Select Conference --</option>
            {conferences.map(conf => (
              <option key={conf.id} value={conf.id}>
                {conf.name}
              </option>
            ))}
          </select>
        </div>

        {selectedConferenceId && tracks.length > 0 && (
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
              Filter by Track:
            </label>
            <select
              value={selectedTrackId}
              onChange={(e) => setSelectedTrackId(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            >
              <option value="">-- All Tracks --</option>
              {tracks.map(track => (
                <option key={track.id} value={track.id}>
                  {track.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {loading && <div style={{ textAlign: 'center', padding: '20px' }}>Loading papers...</div>}

      {!loading && selectedConferenceId && papers.length === 0 && (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          color: '#666',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px'
        }}>
          <h3 style={{ marginTop: 0 }}>No Papers Found</h3>
          <p>No papers have been submitted for this conference yet.</p>
        </div>
      )}

      {!loading && papers.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            backgroundColor: 'white',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Paper ID</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Title</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Author</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Submitted</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Status</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Reviews</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Avg Rating</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {papers.map(paper => (
                <tr key={paper.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: '12px' }}>{paper.id}</td>
                  <td style={{ padding: '12px' }}>{paper.title}</td>
                  <td style={{ padding: '12px' }}>{paper.registration?.user?.name || 'N/A'}</td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>{formatDate(paper.createdAt)}</td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '12px',
                      backgroundColor: getStatusColor(paper.status),
                      color: 'white',
                      fontSize: '12px'
                    }}>
                      {paper.status}
                    </span>
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '12px',
                      backgroundColor: paper.reviewCount > 0 ? '#28a745' : '#6c757d',
                      color: 'white',
                      fontSize: '12px'
                    }}>
                      {paper.reviewCount || 0}
                    </span>
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <strong>{calculateAverageRating(paper.reviews)}</strong>
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <button
                      onClick={() => handleViewReviews(paper)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        marginRight: '8px'
                      }}
                    >
                      View Reviews
                    </button>
                    <button
                      onClick={() => handleChangeStatus(paper)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      Change Status
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Reviews Modal */}
      {showReviewsModal && selectedPaper && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onKeyDown={(e) => {
            // Prevent Ctrl+C, Ctrl+X, Ctrl+A, Ctrl+P
            if (e.ctrlKey || e.metaKey) {
              if (['c', 'x', 'a', 'p'].includes(e.key.toLowerCase())) {
                e.preventDefault();
                return false;
              }
            }
          }}
        >
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '30px',
            maxWidth: '900px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
          }}>
            <div style={{ marginBottom: '20px', borderBottom: '2px solid #f0f0f0', paddingBottom: '15px' }}>
              <h2 style={{ margin: '0 0 10px 0', color: '#333' }}>Reviews for Paper</h2>
              <h3 style={{ margin: '5px 0', color: '#666', fontSize: '18px', fontWeight: 'normal' }}>
                {selectedPaper.title}
              </h3>
              <p style={{ margin: '5px 0', color: '#888', fontSize: '14px' }}>
                {abstractMetadata?.visibleToReviewers && selectedPaper.registration?.user?.name && (
                  <>Author: {selectedPaper.registration.user.name} | </>
                )}
                Average Rating: <strong>{calculateAverageRating(reviews)}</strong> | 
                Total Reviews: <strong>{reviews.length}</strong>
              </p>
              
              <div style={{ 
                marginTop: '15px', 
                padding: '15px', 
                backgroundColor: '#f8f9fa', 
                borderRadius: '4px',
                borderLeft: '4px solid #007bff'
              }}>
                <h4 style={{ margin: '0 0 10px 0', color: '#333', fontSize: '14px', fontWeight: 'bold' }}>
                  Abstract:
                </h4>
                {selectedPaper.abstractText ? (
                  <div
                    style={{ 
                      margin: 0, 
                      color: '#555', 
                      fontSize: '14px', 
                      lineHeight: '1.6', 
                      whiteSpace: 'pre-wrap',
                      userSelect: 'none',
                      WebkitUserSelect: 'none',
                      MozUserSelect: 'none',
                      msUserSelect: 'none',
                      position: 'relative'
                    }}
                    onContextMenu={(e) => { e.preventDefault(); return false; }}
                    onCopy={(e) => { e.preventDefault(); return false; }}
                    onCut={(e) => { e.preventDefault(); return false; }}
                    onDragStart={(e) => { e.preventDefault(); return false; }}
                    onSelectStart={(e) => { e.preventDefault(); return false; }}
                  >
                    {selectedPaper.abstractText}
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      pointerEvents: 'none',
                      opacity: 0.03,
                      fontSize: '48px',
                      fontWeight: 'bold',
                      color: '#000',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transform: 'rotate(-45deg)',
                      whiteSpace: 'nowrap'
                    }}>
                      CONFIDENTIAL
                    </div>
                  </div>
                ) : (
                  <p style={{ margin: 0, color: '#999', fontSize: '14px', fontStyle: 'italic' }}>
                    No abstract provided for this paper.
                  </p>
                )}
              </div>
            </div>

            {reviews.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                <p>No reviews submitted yet for this paper.</p>
              </div>
            ) : (
              <div>
                {reviews.map((review, index) => (
                  <div key={review.id} style={{
                    marginBottom: '20px',
                    padding: '20px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    backgroundColor: '#f8f9fa'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      marginBottom: '15px',
                      paddingBottom: '10px',
                      borderBottom: '1px solid #dee2e6'
                    }}>
                      <div>
                        <h4 style={{ margin: '0 0 5px 0', color: '#333' }}>
                          Review #{index + 1} - Reviewer ID: {review.reviewerId}
                        </h4>
                        <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                          Submitted: {formatDate(review.submittedDate)}
                        </p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ marginBottom: '5px' }}>
                          <span style={{
                            padding: '4px 12px',
                            borderRadius: '12px',
                            backgroundColor: getRecommendationColor(review.recommendation),
                            color: 'white',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}>
                            {review.recommendation?.replace('_', ' ')}
                          </span>
                        </div>
                        {editingReviewId === review.id ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px' }}>
                            <input
                              type="number"
                              min="1"
                              max="10"
                              value={editedRating}
                              onChange={(e) => setEditedRating(e.target.value)}
                              style={{
                                width: '60px',
                                padding: '5px',
                                border: '2px solid #007bff',
                                borderRadius: '4px',
                                fontSize: '16px',
                                textAlign: 'center'
                              }}
                            />
                            <span style={{ fontSize: '14px', color: '#666' }}>/10</span>
                            <button
                              onClick={() => handleSaveOverride(review.id)}
                              disabled={loading}
                              style={{
                                padding: '4px 10px',
                                backgroundColor: '#28a745',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: loading ? 'not-allowed' : 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              Save
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              style={{
                                padding: '4px 10px',
                                backgroundColor: '#6c757d',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div>
                            <div style={{
                              fontSize: '24px',
                              fontWeight: 'bold',
                              color: '#007bff'
                            }}>
                              {review.rating}/10
                            </div>
                            <button
                              onClick={() => handleEditReview(review)}
                              style={{
                                marginTop: '5px',
                                padding: '4px 10px',
                                backgroundColor: '#ffc107',
                                color: '#333',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '11px',
                                fontWeight: 'bold'
                              }}
                            >
                              Override Score
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    <div style={{ marginTop: '15px' }}>
                      <h5 style={{ margin: '0 0 10px 0', color: '#555' }}>Comments:</h5>
                      <div style={{
                        padding: '15px',
                        backgroundColor: 'white',
                        borderRadius: '4px',
                        border: '1px solid #ddd',
                        whiteSpace: 'pre-wrap',
                        lineHeight: '1.6',
                        color: '#333'
                      }}>
                        {review.comments || 'No comments provided'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div style={{ 
              display: 'flex', 
              justifyContent: 'flex-end',
              marginTop: '20px',
              paddingTop: '20px',
              borderTop: '1px solid #f0f0f0'
            }}>
              <button
                onClick={handleCloseReviewsModal}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Change Modal */}
      {showStatusModal && selectedStatusPaper && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '30px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px' }}>Change Paper Status</h3>
            <p style={{ marginBottom: '20px', color: '#666' }}>
              <strong>Paper:</strong> {selectedStatusPaper.title}
            </p>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Select New Status:
              </label>
              <select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value="SUBMITTED">Submitted</option>
                <option value="UNDER_REVIEW">Under Review</option>
                <option value="ACCEPTED">Accepted</option>
                <option value="REJECTED">Rejected</option>
                <option value="MINOR_REVISION">Minor Revision</option>
                <option value="MAJOR_REVISION">Major Revision</option>
              </select>
            </div>
            
            {(newStatus === 'MINOR_REVISION' || newStatus === 'MAJOR_REVISION') && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#dc3545' }}>
                  Revision Feedback: *
                </label>
                <textarea
                  value={revisionFeedback}
                  onChange={(e) => setRevisionFeedback(e.target.value)}
                  placeholder="Please provide detailed feedback on what revisions are needed..."
                  rows={6}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                />
                <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                  This feedback will be sent to the author via email.
                </p>
              </div>
            )}
            
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowStatusModal(false);
                  setRevisionFeedback(''); // Clear feedback on cancel
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveStatus}
                disabled={loading}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                {loading ? 'Updating...' : 'Update Status'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewManagement;
